/**
 * ExtendSessionDialog — Phase 178.6.F.
 *
 * Premium shadcn replacement for the `window.prompt()` extend flow.
 * Minutes stepper with price preview computed proportionally.
 */
import { useEffect, useMemo, useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Clock, Loader2, IndianRupee } from 'lucide-react';
import api from '@/lib/api';
import { track } from '@/lib/analytics';

function durationMinutes(startIso, endIso) {
  return Math.round((new Date(endIso) - new Date(startIso)) / 60000);
}

const PRESETS = [15, 30, 45, 60];

export default function ExtendSessionDialog({ booking, open, onClose, onSuccess }) {
  const [minutes, setMinutes] = useState(30);
  const [submitting, setSubmitting] = useState(false);

  const origDuration = booking ? durationMinutes(booking.start_at, booking.end_at) : 0;
  const origPrice = Number(booking?.price || 0);
  const perMinuteRate = origDuration > 0 ? origPrice / origDuration : 0;
  const additionalPrice = useMemo(
    () => Math.round((perMinuteRate * Number(minutes || 0)) * 100) / 100,
    [perMinuteRate, minutes]
  );
  const newTotal = origPrice + additionalPrice;
  const newEnd = useMemo(() => {
    if (!booking) return null;
    return new Date(new Date(booking.end_at).getTime() + Number(minutes || 0) * 60000);
  }, [booking, minutes]);

  useEffect(() => {
    if (booking && open) {
      setMinutes(30);
      track('dialog_opened', { dialog: 'extend_session', booking_id: booking.id });
    }
  }, [booking, open]);

  const handleSubmit = async () => {
    const m = Number(minutes);
    if (!Number.isFinite(m) || m <= 0) { toast.error('Enter a positive number of minutes'); return; }
    if (m > 240) { toast.error('Maximum extension is 240 minutes (4 hours)'); return; }
    setSubmitting(true);
    try {
      const r = await api.post(`/arenax/bookings/${booking.id}/extend`, { add_minutes: m });
      track('dialog_submitted', { dialog: 'extend_session', booking_id: booking.id, add_minutes: m });
      toast.success(`Extended by ${m} min`);
      onSuccess?.(r.data);
      onClose?.();
    } catch (err) {
      track('dialog_failed', { dialog: 'extend_session', booking_id: booking.id, error: err?.response?.status });
      toast.error(err?.response?.data?.detail || 'Extend failed');
    } finally { setSubmitting(false); }
  };

  const handleCancel = () => {
    track('dialog_cancelled', { dialog: 'extend_session', booking_id: booking?.id });
    onClose?.();
  };

  if (!booking) return null;
  return (
    <Dialog open={open} onOpenChange={(v) => !v && handleCancel()}>
      <DialogContent className="sm:max-w-md" data-testid="extend-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5" /> Extend session
          </DialogTitle>
          <DialogDescription>
            Add time to <strong>{booking.player_name || 'this booking'}</strong>.
            Price recomputes proportionally based on the per-minute rate.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Preset quick picks */}
          <div className="flex flex-wrap gap-2" data-testid="extend-dialog-presets">
            {PRESETS.map((p) => (
              <Button
                key={p}
                size="sm"
                variant={Number(minutes) === p ? 'default' : 'outline'}
                onClick={() => setMinutes(p)}
                data-testid={`extend-dialog-preset-${p}`}
              >
                +{p} min
              </Button>
            ))}
          </div>

          <div>
            <Label htmlFor="extend-minutes" className="text-xs uppercase tracking-wider">
              Custom minutes (1 – 240)
            </Label>
            <Input
              id="extend-minutes"
              type="number"
              min={1}
              max={240}
              step={5}
              value={minutes}
              onChange={(e) => setMinutes(e.target.value)}
              className="mt-1"
              data-testid="extend-dialog-input"
            />
          </div>

          {/* Live price preview */}
          <div
            className="rounded-lg border p-3 space-y-1.5"
            style={{ borderColor: 'hsl(var(--border))', background: 'hsl(var(--muted) / 0.3)' }}
            data-testid="extend-dialog-preview"
          >
            <div className="flex items-center justify-between text-xs"
                 style={{ color: 'hsl(var(--muted-foreground))' }}>
              <span>Current</span>
              <span className="inline-flex items-center font-mono">
                <IndianRupee className="h-3 w-3" />{origPrice.toFixed(0)} · {origDuration} min
              </span>
            </div>
            <div className="flex items-center justify-between text-xs"
                 style={{ color: 'hsl(var(--muted-foreground))' }}>
              <span>+{minutes || 0} min @ ₹{perMinuteRate.toFixed(2)}/min</span>
              <span className="inline-flex items-center font-mono" style={{ color: '#10b981' }}>
                + <IndianRupee className="h-3 w-3" />{additionalPrice.toFixed(2)}
              </span>
            </div>
            <div className="flex items-center justify-between text-sm font-bold pt-1.5 border-t"
                 style={{ borderColor: 'hsl(var(--border))' }}>
              <span>New total</span>
              <span className="inline-flex items-center font-mono">
                <IndianRupee className="h-3.5 w-3.5" />{newTotal.toFixed(2)}
              </span>
            </div>
            {newEnd && (
              <div className="text-[11px] pt-1" style={{ color: 'hsl(var(--muted-foreground))' }}>
                New end: {newEnd.toLocaleString()}
              </div>
            )}
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={handleCancel} disabled={submitting}
                  data-testid="extend-dialog-cancel">
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={submitting}
                  data-testid="extend-dialog-confirm">
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Clock className="h-4 w-4" />}
            Extend
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
